import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, ShieldAlert, History, ListChecks, Monitor, Copy, Ban, CheckCircle2, KeyRound } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api from "../api";
import { useAuth } from "../context/AuthContext";

const ROLES = ["Admin", "Owner", "Accountant", "Tender Exec.", "Office Staff", "CA"];

function InviteLinkBox({ token, onClose }) {
  const link = `${window.location.origin}/accept-invite?token=${token}`;
  const copy = () => {
    navigator.clipboard.writeText(link);
    toast.success("Invite link copied");
  };
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">
        Share this one-time link with the user (valid 72 hours). There's no mail integration yet, so send it
        yourself — WhatsApp, email, whatever's fastest.
      </p>
      <div className="flex items-center gap-2">
        <input readOnly className="sgp-input font-mono text-xs" value={link} />
        <button onClick={copy} className="shrink-0 p-2 rounded-md bg-teal-600 text-white hover:bg-teal-700">
          <Copy className="h-4 w-4" />
        </button>
      </div>
      <button onClick={onClose} className="w-full h-9 rounded-md bg-slate-100 hover:bg-slate-200 text-sm font-medium">
        Done
      </button>
    </div>
  );
}

function UsersTab({ microsoftAvailable }) {
  const [users, setUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "Office Staff", auth_method: "microsoft" });
  const [inviteToken, setInviteToken] = useState(null);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/users", form);
      if (data.auth_method === "password") {
        setInviteToken(data.invite_token);
      } else {
        toast.success(`${form.name} can now sign in with Microsoft (${form.email}).`);
      }
      setForm({ email: "", name: "", role: "Office Staff", auth_method: microsoftAvailable ? "microsoft" : "password" });
      load();
    } catch (err) { toast.error(err.message); }
  };

  const updateUser = async (id, patch) => {
    try {
      await api.patch(`/admin/users/${id}`, patch);
      toast.success("Updated");
      load();
    } catch (err) { toast.error(err.message); }
  };

  const resetPassword = async (id) => {
    try {
      const { data } = await api.post(`/admin/users/${id}/reset-password`);
      setInviteToken(data.invite_token);
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setCreateOpen(true)}
          className="sgp-primary-btn h-9 px-3">
          <UserPlus className="h-4 w-4" /> New User
        </button>
      </div>
      <div className="sgp-table-shell overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr>
            <th className="sgp-th">Name</th><th className="sgp-th">Email</th><th className="sgp-th">Sign-in</th><th className="sgp-th">Role</th>
            <th className="sgp-th">Status</th><th className="sgp-th">Actions</th>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td className="sgp-td">{u.name}</td>
                <td className="sgp-td font-mono text-xs">{u.email}</td>
                <td className="sgp-td text-xs">{u.auth_method === "microsoft" ? "Microsoft" : "Password + 2FA"}</td>
                <td className="sgp-td">
                  <select className="sgp-input h-8 text-xs" value={u.role}
                    onChange={(e) => updateUser(u.id, { role: e.target.value })}>
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td className="sgp-td">
                  {u.active ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-medium">
                      <ShieldAlert className="h-3.5 w-3.5" /> {u.invite_expires_at ? "Invited" : "Disabled"}
                    </span>
                  )}
                </td>
                <td className="sgp-td">
                  <div className="flex gap-2">
                    <button onClick={() => updateUser(u.id, { active: !u.active })}
                      className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 flex items-center gap-1">
                      <Ban className="h-3 w-3" /> {u.active ? "Disable" : "Enable"}
                    </button>
                    {u.auth_method !== "microsoft" && (
                      <button onClick={() => resetPassword(u.id)}
                        className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 flex items-center gap-1">
                        <KeyRound className="h-3 w-3" /> Reset
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add a new user</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div>
              <label className="sgp-label">Name</label>
              <input required className="sgp-input" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="sgp-label">Company email</label>
              <input required type="email" className="sgp-input" placeholder="name@shreegauripharma.in" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <label className="sgp-label">Role</label>
              <select className="sgp-input" value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="sgp-label">Sign-in method</label>
              <select className="sgp-input" value={form.auth_method}
                onChange={(e) => setForm({ ...form, auth_method: e.target.value })}>
                <option value="microsoft" disabled={!microsoftAvailable}>
                  Sign in with Microsoft {microsoftAvailable ? "" : "(not configured yet)"}
                </option>
                <option value="password">Local password + 2FA (invite link)</option>
              </select>
              <p className="text-[11px] text-slate-500 mt-1">
                {form.auth_method === "microsoft"
                  ? "They sign in with their existing shreegauripharma.in Microsoft account — no password to set here."
                  : "You'll get a one-time invite link to send them; they set a password and enroll 2FA."}
              </p>
            </div>
            <DialogFooter>
              <button type="submit" className="w-full h-9 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold">
                {form.auth_method === "microsoft" ? "Add user" : "Create & get invite link"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!inviteToken} onOpenChange={() => setInviteToken(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Invite link ready</DialogTitle></DialogHeader>
          <InviteLinkBox token={inviteToken} onClose={() => setInviteToken(null)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LoginHistoryTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/login-history").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="sgp-card overflow-hidden max-h-[32rem] overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0"><tr>
          <th className="sgp-th">Time</th><th className="sgp-th">Email</th><th className="sgp-th">Result</th>
          <th className="sgp-th">IP</th><th className="sgp-th">Note</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="sgp-td whitespace-nowrap text-xs">{new Date(r.at).toLocaleString()}</td>
              <td className="sgp-td font-mono text-xs">{r.email}</td>
              <td className="sgp-td">
                <span className={
                  r.result === "success" ? "text-emerald-700" : r.result === "locked" ? "text-red-600" : "text-amber-600"
                }>{r.result}</span>
              </td>
              <td className="sgp-td text-xs">{r.ip}</td>
              <td className="sgp-td text-xs text-slate-500">{r.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChangeLogTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/change-log").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="sgp-card overflow-hidden max-h-[32rem] overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0"><tr>
          <th className="sgp-th">Time</th><th className="sgp-th">User</th><th className="sgp-th">Entity</th>
          <th className="sgp-th">Action</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="sgp-td whitespace-nowrap text-xs">{new Date(r.at).toLocaleString()}</td>
              <td className="sgp-td font-mono text-xs">{r.user_email}</td>
              <td className="sgp-td text-xs">{r.entity} · {r.entity_id?.slice(0, 8)}</td>
              <td className="sgp-td text-xs font-medium">{r.action}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SessionsTab() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/sessions").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const revoke = async (id) => {
    await api.post(`/admin/sessions/${id}/revoke`);
    toast.success("Session signed out");
    load();
  };
  return (
    <div className="sgp-table-shell overflow-hidden">
      <table className="w-full text-sm">
        <thead><tr>
          <th className="sgp-th">User</th><th className="sgp-th">Device</th><th className="sgp-th">IP</th>
          <th className="sgp-th">Last active</th><th className="sgp-th"></th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="sgp-td">{r.user_name || r.user_email}</td>
              <td className="sgp-td text-xs">{r.device_name || r.user_agent?.slice(0, 30)}</td>
              <td className="sgp-td text-xs">{r.ip}</td>
              <td className="sgp-td text-xs">{new Date(r.last_seen).toLocaleString()}</td>
              <td className="sgp-td">
                <button onClick={() => revoke(r.id)}
                  className="text-xs px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50">
                  Sign out
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const LOGIN_ACTIVITY_OWNER = "r.pandey@shreegauripharma.in";

export default function Admin() {
  const { user } = useAuth();
  const [microsoftAvailable, setMicrosoftAvailable] = useState(false);
  const canSeePrivateActivity = user?.email?.toLowerCase() === LOGIN_ACTIVITY_OWNER;

  useEffect(() => {
    api.get("/auth/bootstrap-status").then((r) => setMicrosoftAvailable(!!r.data.microsoft_sso)).catch(() => {});
  }, []);

  if (user?.role !== "Admin") {
    return <p className="text-sm text-slate-500 py-10 text-center">Admin access only.</p>;
  }
  return (
    <div className="sgp-page" data-testid="admin-page">
      <div>
        <h1 className="sgp-page-title">Users & Security</h1>
        <p className="sgp-page-subtitle">Accounts, roles, login history, change log, active sessions.</p>
      </div>
      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users" className="flex items-center gap-1.5"><UserPlus className="h-3.5 w-3.5" /> Users</TabsTrigger>
          {canSeePrivateActivity && <TabsTrigger value="logins" className="flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> Login History</TabsTrigger>}
          <TabsTrigger value="changes" className="flex items-center gap-1.5"><ListChecks className="h-3.5 w-3.5" /> Change Log</TabsTrigger>
          {canSeePrivateActivity && <TabsTrigger value="sessions" className="flex items-center gap-1.5"><Monitor className="h-3.5 w-3.5" /> Sessions</TabsTrigger>}
        </TabsList>
        <TabsContent value="users"><UsersTab microsoftAvailable={microsoftAvailable} /></TabsContent>
        {canSeePrivateActivity && <TabsContent value="logins"><LoginHistoryTab /></TabsContent>}
        <TabsContent value="changes"><ChangeLogTab /></TabsContent>
        {canSeePrivateActivity && <TabsContent value="sessions"><SessionsTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
